import { createContext, useContext, useEffect } from 'react';
import type { RahTile } from '../../components/organisms/RahTopbar';

/**
 * Shell context, kept in its own module on purpose.
 *
 * Screens need `useRahFunctionTiles`, and `RahShell` needs the menu registry,
 * which reaches back into `customize/registry` -> the tenant bundle -> the
 * tenant's routes -> the screens. Importing the hook straight from `RahShell`
 * closes that loop and the screen module evaluates to `undefined`. Splitting
 * the context out breaks the cycle: this module imports nothing but a type.
 */

export interface RahShellContextValue {
  /** Left topbar zone (R-04): metrics for the screen in view. */
  setFunctionTiles: (tiles: RahTile[]) => void;
  /** 拠点 currently ticked in the topbar 担当拠点フィルター. */
  selectedClinics: string[];
}

export const RahShellContext = createContext<RahShellContextValue | null>(null);

/**
 * Publish this screen's topbar metrics for as long as the screen is mounted.
 * Outside the RAH shell it is a no-op, so screens stay portable.
 */
export function useRahFunctionTiles(tiles: RahTile[]): void {
  const setFunctionTiles = useContext(RahShellContext)?.setFunctionTiles;

  // Tiles are usually an inline literal, so key the effect on content rather
  // than on the array identity, which changes every render.
  const signature = JSON.stringify(tiles.map(t => [t.key, t.label, t.value, t.unit, t.tone, t.group]));

  useEffect(() => {
    if (!setFunctionTiles) return;
    setFunctionTiles(tiles);
    return () => setFunctionTiles([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFunctionTiles, signature]);
}

/**
 * The 拠点 the topbar filter currently scopes to.
 *
 * Screens filter their rows by this. Outside the RAH shell it returns `null`,
 * meaning "no scope filter" — screens then show everything rather than nothing.
 */
export function useRahClinicScope(): string[] | null {
  return useContext(RahShellContext)?.selectedClinics ?? null;
}
